import { logger } from '../../utils/logger.js'

const log = logger.create('webgl-tiles')

const VERTEX_SRC = `
attribute vec2 a_pos;
varying vec2 v_texCoord;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
  v_texCoord = a_pos * 0.5 + 0.5;
}
`

type UniformValue = number | number[] | WebGLTexture

interface TextureBinding {
  texture: WebGLTexture
  unit: number
}

class WebGLTileRenderer {
  // Retained to prevent GC of the WebGL context's backing canvas
  private canvas: OffscreenCanvas | HTMLCanvasElement | null = null
  private gl: WebGLRenderingContext | WebGL2RenderingContext | null = null
  private vertexShader: WebGLShader | null = null
  private programs = new Map<string, WebGLProgram>()
  private quadBuffer: WebGLBuffer | null = null
  private fbo: WebGLFramebuffer | null = null
  private fboTexture: WebGLTexture | null = null
  private fboWidth = 0
  private fboHeight = 0
  private _available = false
  private _contextGeneration = 0

  get available(): boolean {
    return this._available
  }

  get contextGeneration(): number {
    return this._contextGeneration
  }

  init(): boolean {
    if (this._available) return true

    try {
      const { canvas, gl } = this.createContext()
      if (!gl) {
        log.info('no WebGL context available, using CPU fallback')
        return false
      }

      this.canvas = canvas
      this.gl = gl
      log.info(
        `initialized ${gl instanceof WebGL2RenderingContext ? 'WebGL2' : 'WebGL1'} on ${canvas instanceof OffscreenCanvas ? 'OffscreenCanvas' : 'HTMLCanvasElement'}`
      )

      canvas.addEventListener('webglcontextlost', this.onContextLost as EventListener)
      canvas.addEventListener('webglcontextrestored', this.onContextRestored as EventListener)

      this.setupQuad()
      this.vertexShader = this.compileShader(VERTEX_SRC, gl.VERTEX_SHADER)
      if (!this.vertexShader) {
        this.destroy()
        return false
      }

      this._available = true
      return true
    } catch (e) {
      log.warn('init failed, using CPU fallback:', e)
      this.destroy()
      return false
    }
  }

  private createContext(): {
    canvas: OffscreenCanvas | HTMLCanvasElement
    gl: WebGLRenderingContext | WebGL2RenderingContext | null
  } {
    const opts = { premultipliedAlpha: false }

    // Try OffscreenCanvas first (works on desktop, some mobile)
    try {
      const offscreen = new OffscreenCanvas(256, 256)
      const gl = offscreen.getContext('webgl2', opts) ?? offscreen.getContext('webgl', opts)
      if (gl) return { canvas: offscreen, gl }
    } catch {
      // OffscreenCanvas may not support WebGL contexts
    }

    // Fall back to hidden HTMLCanvasElement (broader WebGL support)
    const el = document.createElement('canvas')
    el.width = 256
    el.height = 256
    el.style.display = 'none'
    const gl =
      (el.getContext('webgl2', opts) as WebGL2RenderingContext | null) ??
      (el.getContext('webgl', opts) as WebGLRenderingContext | null)
    return { canvas: el, gl }
  }

  compileProgram(fragmentSrc: string, key: string): WebGLProgram | null {
    const cached = this.programs.get(key)
    if (cached) return cached

    const gl = this.gl
    if (!gl || !this.vertexShader) return null

    const fragShader = this.compileShader(fragmentSrc, gl.FRAGMENT_SHADER)
    if (!fragShader) return null

    const program = gl.createProgram()
    if (!program) {
      gl.deleteShader(fragShader)
      return null
    }

    gl.attachShader(program, this.vertexShader)
    gl.attachShader(program, fragShader)
    gl.linkProgram(program)
    gl.deleteShader(fragShader)

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program)
      return null
    }

    this.programs.set(key, program)
    return program
  }

  uploadTexture(
    pixels: Uint8ClampedArray,
    width: number,
    height: number,
    linear = false
  ): WebGLTexture | null {
    const gl = this.gl
    if (!gl) return null

    const texture = gl.createTexture()
    if (!texture) return null

    // linear=true bilinearly interpolates the source between texels — used for the slope DEM
    // so the slope field is continuous instead of piecewise-constant per DEM cell (smoother
    // shading edges). Defaults to NEAREST for exact-value sources (hillshade, sun-exposure).
    const filter = linear ? gl.LINEAR : gl.NEAREST
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels)

    const err = gl.getError()
    if (err !== gl.NO_ERROR) {
      log.warn(`uploadTexture GL error: 0x${err.toString(16)}`)
      gl.deleteTexture(texture)
      if (err === gl.OUT_OF_MEMORY) this.handleSilentContextLoss()
      return null
    }

    return texture
  }

  renderToTexture(
    program: WebGLProgram,
    uniforms: Record<string, UniformValue>,
    textures: Record<string, TextureBinding>,
    width: number,
    height: number
  ): WebGLTexture | null {
    const gl = this.gl
    if (!gl || !this.quadBuffer || gl.isContextLost()) {
      this.handleSilentContextLoss()
      return null
    }

    const outTexture = gl.createTexture()
    if (!outTexture) return null

    gl.bindTexture(gl.TEXTURE_2D, outTexture)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)

    const fbo = gl.createFramebuffer()
    if (!fbo) {
      gl.deleteTexture(outTexture)
      return null
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, outTexture, 0)
    gl.viewport(0, 0, width, height)
    gl.useProgram(program)

    for (const [name, binding] of Object.entries(textures)) {
      gl.activeTexture(gl.TEXTURE0 + binding.unit)
      gl.bindTexture(gl.TEXTURE_2D, binding.texture)
      gl.uniform1i(gl.getUniformLocation(program, name), binding.unit)
    }

    for (const [name, value] of Object.entries(uniforms)) {
      const loc = gl.getUniformLocation(program, name)
      if (!loc) continue
      if (typeof value === 'number') {
        gl.uniform1f(loc, value)
      } else if (Array.isArray(value)) {
        if (value.length === 2) gl.uniform2fv(loc, value)
        else if (value.length === 3) gl.uniform3fv(loc, value)
        else if (value.length === 4) gl.uniform4fv(loc, value)
      }
    }

    const posLoc = gl.getAttribLocation(program, 'a_pos')
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.enableVertexAttribArray(posLoc)
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.deleteFramebuffer(fbo)

    if (gl.isContextLost()) {
      this.handleSilentContextLoss()
      return null
    }

    return outTexture
  }

  renderToPixels(
    program: WebGLProgram,
    uniforms: Record<string, UniformValue>,
    textures: Record<string, TextureBinding>,
    width: number,
    height: number
  ): Uint8ClampedArray | null {
    const gl = this.gl
    if (!gl || !this.quadBuffer || gl.isContextLost()) {
      this.handleSilentContextLoss()
      return null
    }

    this.ensureFbo(width, height)
    if (!this.fbo) return null

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo)
    gl.viewport(0, 0, width, height)
    gl.useProgram(program)

    // Bind textures
    for (const [name, binding] of Object.entries(textures)) {
      gl.activeTexture(gl.TEXTURE0 + binding.unit)
      gl.bindTexture(gl.TEXTURE_2D, binding.texture)
      gl.uniform1i(gl.getUniformLocation(program, name), binding.unit)
    }

    // Set uniforms
    for (const [name, value] of Object.entries(uniforms)) {
      const loc = gl.getUniformLocation(program, name)
      if (!loc) continue
      if (typeof value === 'number') {
        gl.uniform1f(loc, value)
      } else if (Array.isArray(value)) {
        if (value.length === 2) gl.uniform2fv(loc, value)
        else if (value.length === 3) gl.uniform3fv(loc, value)
        else if (value.length === 4) gl.uniform4fv(loc, value)
      }
    }

    // Draw full-screen quad
    const posLoc = gl.getAttribLocation(program, 'a_pos')
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.enableVertexAttribArray(posLoc)
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

    const output = new Uint8ClampedArray(width * height * 4)
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, output)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)

    if (gl.isContextLost()) {
      this.handleSilentContextLoss()
      return null
    }

    return output
  }

  deleteTexture(texture: WebGLTexture): void {
    this.gl?.deleteTexture(texture)
  }

  setUniform1i(program: WebGLProgram, name: string, value: number): void {
    const gl = this.gl
    if (!gl) return
    gl.useProgram(program)
    const loc = gl.getUniformLocation(program, name)
    if (loc) gl.uniform1i(loc, value)
  }

  private handleSilentContextLoss(): void {
    if (!this._available) return
    this._available = false
    this._contextGeneration++
    this.programs.clear()
    this.vertexShader = null
    this.quadBuffer = null
    this.fbo = null
    this.fboTexture = null
    this.fboWidth = 0
    this.fboHeight = 0
    this.gl = null
    this.canvas = null
    log.warn('WebGL context lost (detected silently), will re-init on next use')
  }

  destroy(): void {
    const gl = this.gl
    if (gl) {
      for (const prog of this.programs.values()) gl.deleteProgram(prog)
      this.programs.clear()
      if (this.vertexShader) gl.deleteShader(this.vertexShader)
      if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer)
      if (this.fboTexture) gl.deleteTexture(this.fboTexture)
      if (this.fbo) gl.deleteFramebuffer(this.fbo)
    }
    this.vertexShader = null
    this.quadBuffer = null
    this.fbo = null
    this.fboTexture = null
    this.gl = null
    this.canvas = null
    this._available = false
  }

  private compileShader(source: string, type: number): WebGLShader | null {
    const gl = this.gl
    if (!gl) return null

    const shader = gl.createShader(type)
    if (!shader) return null

    gl.shaderSource(shader, source)
    gl.compileShader(shader)

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      log.error('shader compile error:', gl.getShaderInfoLog(shader))
      gl.deleteShader(shader)
      return null
    }

    return shader
  }

  private setupQuad(): void {
    const gl = this.gl
    if (!gl) return

    this.quadBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
  }

  private ensureFbo(width: number, height: number): void {
    const gl = this.gl
    if (!gl) return

    if (this.fbo && this.fboWidth === width && this.fboHeight === height) return

    if (this.canvas) {
      this.canvas.width = width
      this.canvas.height = height
    }

    if (this.fboTexture) gl.deleteTexture(this.fboTexture)
    if (this.fbo) gl.deleteFramebuffer(this.fbo)

    this.fboTexture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, this.fboTexture)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)

    this.fbo = gl.createFramebuffer()
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.fboTexture, 0)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)

    this.fboWidth = width
    this.fboHeight = height
  }

  private onContextLost = (e: Event): void => {
    e.preventDefault()
    this._available = false
    this._contextGeneration++
    this.programs.clear()
    this.vertexShader = null
    this.quadBuffer = null
    this.fbo = null
    this.fboTexture = null
    log.warn('WebGL context lost')
  }

  private onContextRestored = (): void => {
    try {
      this.setupQuad()
      this.vertexShader = this.compileShader(VERTEX_SRC, this.gl!.VERTEX_SHADER)
      if (this.vertexShader) {
        this._available = true
        log.info('WebGL context restored')
      }
    } catch {
      this._available = false
    }
  }
}

export const webglTileRenderer = new WebGLTileRenderer()
